import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { api, errMsg } from "../lib/api";

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testid}><h2 className="text-lg sm:text-xl">{title}</h2><div className="mt-5">{children}</div></section>
);

const Categories = () => {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("group");
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/categories", { slug: name, name, type }); setName(""); load(); toast.success("Category added"); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (slug) => { if (window.confirm("Delete this category? It will be removed from all tours.")) { await api.delete(`/admin/categories/${slug}`); load(); } };
  return (
    <Card title="Tour categories" testid="admin-categories">
      <ul className="flex flex-wrap gap-2">{cats.map((c) => <li key={c.slug} className="flex items-center gap-2 rounded-full border border-slate-200 py-1.5 pl-3 pr-1.5 text-sm" data-testid={`category-${c.slug}`}>{c.name}<span className="text-xs text-slate-400">{c.type}</span><button onClick={() => del(c.slug)} className="rounded-full p-1 text-red-600 hover:bg-red-50" data-testid={`category-delete-${c.slug}`}><Trash2 className="h-3.5 w-3.5" /></button></li>)}</ul>
      <form onSubmit={add} className="mt-4 flex flex-wrap gap-2">
        <input className="field max-w-xs" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="category-name-input" />
        <select className="field max-w-[140px]" value={type} onChange={(e) => setType(e.target.value)}><option value="group">Group</option><option value="family">Family</option></select>
        <button className="btn-brand !py-2.5" data-testid="category-add-btn"><Plus className="h-4 w-4" />Add</button>
      </form>
    </Card>
  );
};

const EMPTY_T = { name: "", trip: "", text: "", verified: false, is_published: false };
const Testimonials = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(EMPTY_T);
  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/testimonials", f); setF(EMPTY_T); load(); toast.success("Testimonial saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  const update = async (t, patch) => { try { await api.put(`/admin/testimonials/${t.id}`, { ...t, ...patch }); load(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card title="Testimonials (genuine reviews only)" testid="admin-testimonials">
      <p className="text-sm text-slate-500">The testimonials section stays hidden on the website until at least one verified review is published.</p>
      <ul className="mt-4 space-y-3">{list.map((t) => (
        <li key={t.id} className="rounded-xl border border-slate-200 p-4 text-sm" data-testid={`testimonial-row-${t.id}`}>
          <p className="font-semibold">{t.name} <span className="font-normal text-slate-500">{t.trip}</span></p><p className="mt-1 text-slate-600">{t.text}</p>
          <div className="mt-3 flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2"><Switch checked={t.verified} onCheckedChange={(v) => update(t, { verified: v, is_published: v && t.is_published })} />Verified</label>
            <label className="flex items-center gap-2"><Switch checked={t.is_published} onCheckedChange={(v) => update(t, { is_published: v })} />Published</label>
            <button onClick={async () => { await api.delete(`/admin/testimonials/${t.id}`); load(); }} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        </li>))}</ul>
      <form onSubmit={save} className="mt-5 grid gap-3 md:grid-cols-2">
        <input className="field" placeholder="Traveller name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="testimonial-name-input" />
        <input className="field" placeholder="Trip (e.g. Spiti Valley, June)" value={f.trip} onChange={(e) => setF({ ...f, trip: e.target.value })} />
        <textarea className="field md:col-span-2" rows={3} placeholder="Review text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="testimonial-text-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} data-testid="testimonial-verified-switch" />I confirm this is a genuine customer review</label>
        <button className="btn-brand justify-self-start !py-2.5" data-testid="testimonial-add-btn"><Plus className="h-4 w-4" />Add testimonial</button>
      </form>
    </Card>
  );
};

const Settings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { try { await api.put("/admin/settings", { ...s, gallery: (s.gallery || []).map((x) => x.trim()).filter(Boolean) }); toast.success("Settings saved — reload the site to see changes"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Social links & Instagram gallery" testid="admin-settings">
      <div className="grid gap-3 md:grid-cols-3">
        {["instagram_url", "facebook_url", "youtube_url"].map((k) => <div key={k}><label className="label">{k.split("_")[0]} URL</label><input className="field" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} /></div>)}
      </div>
      <label className="label mt-4">Gallery images (one URL per line; use authorised photos)</label>
      <textarea className="field" rows={6} value={(s.gallery || []).join("\n")} onChange={(e) => setS({ ...s, gallery: e.target.value.split("\n") })} data-testid="settings-gallery" />
      <button onClick={save} className="btn-brand mt-4 !py-2.5" data-testid="settings-save-btn">Save settings</button>
    </Card>
  );
};

export default function AdminContent() {
  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <h1 className="text-2xl sm:text-3xl">Content & Settings</h1>
      <Categories /><Testimonials /><Settings />
    </div>
  );
}
